import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  Alert,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Security,
  Email,
  CheckCircle,
  Error,
  ArrowBack,
  ArrowForward,
  Password,
  VpnKey
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import { change_pass, generate_otp, verify_otp, reset_password } from '../api/authHandlers';

const TOKEN_COOKIE_KEY = 'token';
const SECRET_KEY = 'tabtrips_hotel_secret';

function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

const ChangePasswordDialog = ({ open, onClose, userEmail, userId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Dialog states
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [success, setSuccess] = useState('');
  
  // Password visibility states
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetNewPassword, setShowResetNewPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);
  
  // Form states
  const [changePasswordForm, setChangePasswordForm] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [forgotPasswordForm, setForgotPasswordForm] = useState({
    email: userEmail || '',
    otp: '',
    new_password: '',
    confirm_password: ''
  });
  
  // OTP states
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  
  // Password validation states
  const [passwordValidation, setPasswordValidation] = useState({
    length: false
  });

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setErrorMessage(''); // Clear any previous errorMessages
      setSuccess('');
      setOtpSent(false);
      setOtpVerified(false);
      setOtpCooldown(0);
      setChangePasswordForm({
        old_password: '',
        new_password: '',
        confirm_password: ''
      });
      setForgotPasswordForm({
        email: userEmail || '',
        otp: '',
        new_password: '',
        confirm_password: ''
      });
      setPasswordValidation({
        length: false
      });
    }
  }, [open, userEmail]);

  // OTP cooldown timer
  useEffect(() => {
    let interval;
    if (otpCooldown > 0) {
      interval = setInterval(() => {
        setOtpCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpCooldown]);

  // Password validation - just 6 characters minimum
  const validatePassword = (password) => {
    const validation = {
      length: password.length >= 6
    };
    setPasswordValidation(validation);
    return password.length >= 6;
  };

  const isPasswordValid = passwordValidation.length;
  const isChangePasswordFormValid = 
    changePasswordForm.old_password && 
    changePasswordForm.new_password && 
    changePasswordForm.confirm_password &&
    changePasswordForm.new_password === changePasswordForm.confirm_password &&
    isPasswordValid;

  const isResetPasswordFormValid = 
    forgotPasswordForm.new_password && 
    forgotPasswordForm.confirm_password &&
    forgotPasswordForm.new_password === forgotPasswordForm.confirm_password &&
    isPasswordValid;

  // Handle form changes
  const handleChangePasswordChange = (field, value) => {
    setChangePasswordForm(prev => ({ ...prev, [field]: value }));
    if (field === 'new_password') {
      validatePassword(value);
    }
    setErrorMessage('');
  };

  const handleForgotPasswordChange = (field, value) => {
    setForgotPasswordForm(prev => ({ ...prev, [field]: value }));
    if (field === 'new_password') {
      validatePassword(value);
    }
    setErrorMessage('');
  };

  // Handle change password
  const handleChangePassword = async () => {
    if (!isChangePasswordFormValid) return;

    const token = getDecryptedToken();
    if (!token) {
      setErrorMessage('Session expired. Please log in again.');
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const response = await change_pass({
        token,
        user_id: userId,
        old_password: changePasswordForm.old_password,
        new_password: changePasswordForm.new_password,
        confirm_password: changePasswordForm.confirm_password
      });
      
      if (response.status) {
        setSuccess('Password changed successfully!');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setErrorMessage(response.message || 'Failed to change password');
      }
    } catch (error) {
      console.error('Change password error:', error);
      setErrorMessage('An error occurred while changing password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle forgot password - send OTP
  const handleSendOTP = async () => {
    if (!forgotPasswordForm.email) {
      setErrorMessage('Please enter your email address');
      return;
    }
    
    setLoading(true);
    setErrorMessage('');
    
    try {
      const response = await generate_otp(forgotPasswordForm.email);
      
      if (response.status) {
        setOtpSent(true);
        setOtpCooldown(60); // 60 seconds cooldown
        setErrorMessage(''); // Clear any previous errorMessages
        setSuccess('OTP sent successfully to your email!');
        setActiveStep(2); // Move to OTP verification step
      } else {
        setErrorMessage(response.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      setErrorMessage('An error occurred while sending OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP verification
  const handleVerifyOTP = async () => {
    if (!forgotPasswordForm.otp || forgotPasswordForm.otp.length !== 6) {
      setErrorMessage('Please enter a valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    setErrorMessage('');
    
    try {
      const response = await verify_otp({
        email: forgotPasswordForm.email,
        otp: forgotPasswordForm.otp
      });
      
      if (response.status) {
        setOtpVerified(true);
        setErrorMessage(''); // Clear any previous errorMessages
        setSuccess('OTP verified successfully! You can now set a new password.');
        setActiveStep(3); // Move to new password step
      } else {
        setErrorMessage(response.message || 'Invalid OTP');
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      setErrorMessage('An error occurred while verifying OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle password reset
  const handleResetPassword = async () => {
    if (!isResetPasswordFormValid) return;
    
    setLoading(true);
    setErrorMessage('');
    
    try {
      const response = await reset_password({
        email: forgotPasswordForm.email,
        new_password: forgotPasswordForm.new_password,
        confirm_password: forgotPasswordForm.confirm_password
      });
      
      if (response.status) {
        setSuccess('Password reset successfully! You can now login with your new password.');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setErrorMessage(response.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setErrorMessage('An error occurred while resetting password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    setErrorMessage(''); // Clear any errorMessages when closing
    setSuccess('');
    onClose();
  };


  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setErrorMessage('');
    setSuccess('');
  };


  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          border: '1px solid rgba(0,0,0,0.05)',
          minHeight: isMobile ? '90vh' : 'auto'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        color: '#2E4057',
        fontWeight: 600,
        pb: 1,
        borderBottom: '1px solid rgba(0,0,0,0.08)'
      }}>
        <Security sx={{ color: '#FF7700' }} />
        Change Password
      </DialogTitle>

      <DialogContent sx={{ pt: 4, pb: 3 }}>
        <AnimatePresence mode="wait">
          {activeStep === 0 && (
            <motion.div
              key="change-password"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Change Password Form */}
              <Box sx={{ mb: 3, mt: 2 }}>
                
                <TextField
                  fullWidth
                  label="Current Password"
                  type={showOldPassword ? 'text' : 'password'}
                  value={changePasswordForm.old_password}
                  onChange={(e) => handleChangePasswordChange('old_password', e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Password sx={{ color: '#FF7700' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowOldPassword(!showOldPassword)}
                          edge="end"
                        >
                          {showOldPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={changePasswordForm.new_password}
                  onChange={(e) => handleChangePasswordChange('new_password', e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKey sx={{ color: '#FF7700' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={changePasswordForm.confirm_password}
                  onChange={(e) => handleChangePasswordChange('confirm_password', e.target.value)}
                  sx={{ mb: 2 }}
                  error={Boolean(
                    changePasswordForm.confirm_password &&
                      changePasswordForm.new_password !== changePasswordForm.confirm_password
                  )}
                  helperText={
                    changePasswordForm.confirm_password &&
                    changePasswordForm.new_password !== changePasswordForm.confirm_password
                      ? "Passwords do not match"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKey sx={{ color: '#FF7700' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Password Strength Indicator */}
                {changePasswordForm.new_password && (
                  <Paper sx={{ p: 2, mb: 2, bgcolor: 'rgba(255,119,0,0.05)' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Password Requirements:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {passwordValidation.length ? (
                        <CheckCircle sx={{ color: '#4CAF50', fontSize: 16 }} />
                      ) : (
                        <Error sx={{ color: '#F44336', fontSize: 16 }} />
                      )}
                      <Typography variant="body2" sx={{ color: passwordValidation.length ? '#4CAF50' : '#F44336' }}>
                        At least 6 characters
                      </Typography>
                    </Box>
                  </Paper>
                )}
              </Box>

              {/* Forgot Password Link */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Forgot your current password?
                </Typography>
                <Button
                  variant="text"
                  onClick={() => {
                    setActiveStep(1);
                    setErrorMessage(''); // Clear any errorMessages when switching to reset password
                  }}
                  sx={{ 
                    color: '#FF7700',
                    textTransform: 'none',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'rgba(255,119,0,0.05)'
                    }
                  }}
                >
                  Reset Password Instead
                </Button>
              </Box>

              {/* OTP Section - Only show after OTP is sent */}
              {otpSent && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(255,119,0,0.05)', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ 
                    mb: 2, 
                    color: '#2E4057',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    <Security sx={{ color: '#FF7700' }} />
                    Enter OTP
                  </Typography>
                  
                  <Typography variant="body2" sx={{ 
                    mb: 2, 
                    color: 'text.secondary'
                  }}>
                    Enter the 6-digit verification code sent to:
                  </Typography>

                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'rgba(255,119,0,0.1)', 
                    borderRadius: 2,
                    border: '1px solid rgba(255,119,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2
                  }}>
                    <Email sx={{ color: '#FF7700', fontSize: '1.2rem' }} />
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {forgotPasswordForm.email}
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    label="6-digit OTP"
                    value={forgotPasswordForm.otp}
                    onChange={(e) => handleForgotPasswordChange('otp', e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    error={!!errorMessage && errorMessage.includes('OTP')}
                    helperText={errorMessage && errorMessage.includes('OTP') ? errorMessage : ''}
                    inputProps={{ 
                      maxLength: 6,
                      style: { 
                        textAlign: 'center',
                        fontSize: '1.2rem',
                        letterSpacing: '0.5rem',
                        fontWeight: 600
                      }
                    }}
                    sx={{ mb: 2 }}
                  />

                  {otpCooldown > 0 && (
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary',
                      display: 'block',
                      textAlign: 'center',
                      mb: 2
                    }}>
                      Resend OTP in {otpCooldown} seconds
                    </Typography>
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleVerifyOTP}
                    disabled={loading || !forgotPasswordForm.otp || forgotPasswordForm.otp.length !== 6}
                    sx={{ 
                      borderRadius: 2,
                      py: 1.2,
                      textTransform: 'none',
                      fontWeight: 600,
                      bgcolor: '#FF7700',
                      '&:hover': {
                        bgcolor: '#E56900'
                      },
                      '&:disabled': {
                        bgcolor: 'rgba(255,119,0,0.3)',
                        color: 'rgba(255,255,255,0.5)'
                      }
                    }}
                  >
                    {loading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} color="inherit" />
                        Verifying...
                      </Box>
                    ) : (
                      'Verify OTP'
                    )}
                  </Button>
                </Box>
              )}
            </motion.div>
          )}

          {activeStep === 1 && (
            <motion.div
              key="forgot-password"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  mb: 2, 
                  color: '#2E4057',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Email sx={{ color: '#FF7700' }} />
                  Reset Password
                </Typography>
                
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={forgotPasswordForm.email}
                  disabled
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: '#FF7700' }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Typography variant="body2" sx={{ 
                  color: 'text.secondary',
                  mb: 2,
                  p: 2,
                  bgcolor: 'rgba(255,119,0,0.05)',
                  borderRadius: 2,
                  border: '1px solid rgba(255,119,0,0.2)'
                }}>
                  We'll send a verification code to your registered email address to reset your password.
                </Typography>
              </Box>
            </motion.div>
          )}

          {activeStep === 2 && (
            <motion.div
              key="otp-verification"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  mb: 2, 
                  color: '#2E4057',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <Security sx={{ color: '#FF7700' }} />
                  Verify OTP
                </Typography>
                
                <Typography variant="body2" sx={{ 
                  mb: 2, 
                  color: 'text.secondary'
                }}>
                  Enter the 6-digit verification code sent to:
                </Typography>

                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'rgba(255,119,0,0.05)', 
                  borderRadius: 2,
                  border: '1px solid rgba(255,119,0,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2
                }}>
                  <Email sx={{ color: '#FF7700', fontSize: '1.2rem' }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {forgotPasswordForm.email}
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="6-digit OTP"
                  value={forgotPasswordForm.otp}
                  onChange={(e) => handleForgotPasswordChange('otp', e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  error={!!errorMessage && errorMessage.includes('OTP')}
                  helperText={errorMessage && errorMessage.includes('OTP') ? errorMessage : ''}
                  inputProps={{ 
                    maxLength: 6,
                    style: { 
                      textAlign: 'center',
                      fontSize: '1.2rem',
                      letterSpacing: '0.5rem',
                      fontWeight: 600
                    }
                  }}
                  sx={{ mb: 2 }}
                />

                {otpCooldown > 0 && (
                  <Typography variant="caption" sx={{ 
                    color: 'text.secondary',
                    display: 'block',
                    textAlign: 'center',
                    mb: 2
                  }}>
                    Resend OTP in {otpCooldown} seconds
                  </Typography>
                )}
              </Box>
            </motion.div>
          )}

          {activeStep === 3 && (
            <motion.div
              key="reset-password"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  mb: 2, 
                  color: '#2E4057',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}>
                  <VpnKey sx={{ color: '#FF7700' }} />
                  Set New Password
                </Typography>
                
                <TextField
                  fullWidth
                  label="New Password"
                  type={showResetNewPassword ? 'text' : 'password'}
                  value={forgotPasswordForm.new_password}
                  onChange={(e) => handleForgotPasswordChange('new_password', e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKey sx={{ color: '#FF7700' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowResetNewPassword(!showResetNewPassword)}
                          edge="end"
                        >
                          {showResetNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type={showResetConfirmPassword ? 'text' : 'password'}
                  value={forgotPasswordForm.confirm_password}
                  onChange={(e) => handleForgotPasswordChange('confirm_password', e.target.value)}
                  sx={{ mb: 2 }}
                  error={Boolean(
                    forgotPasswordForm.confirm_password &&
                      forgotPasswordForm.new_password !== forgotPasswordForm.confirm_password
                  )}
                  helperText={
                    forgotPasswordForm.confirm_password &&
                    forgotPasswordForm.new_password !== forgotPasswordForm.confirm_password
                      ? "Passwords do not match"
                      : ""
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKey sx={{ color: '#FF7700' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowResetConfirmPassword(!showResetConfirmPassword)}
                          edge="end"
                        >
                          {showResetConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Password Strength Indicator */}
                {forgotPasswordForm.new_password && (
                  <Paper sx={{ p: 2, mb: 2, bgcolor: 'rgba(255,119,0,0.05)' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Password Requirements:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {passwordValidation.length ? (
                        <CheckCircle sx={{ color: '#4CAF50', fontSize: 16 }} />
                      ) : (
                        <Error sx={{ color: '#F44336', fontSize: 16 }} />
                      )}
                      <Typography variant="body2" sx={{ color: passwordValidation.length ? '#4CAF50' : '#F44336' }}>
                        At least 6 characters
                      </Typography>
                    </Box>
                  </Paper>
                )}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error/Success Messages */}
        <AnimatePresence>
          {errorMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {errorMessage}
              </Alert>
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                {success}
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        gap: 2,
        borderTop: '1px solid rgba(0,0,0,0.08)',
        bgcolor: 'rgba(0,0,0,0.02)'
      }}>
        {activeStep === 0 && (
          <>
            <Button
              onClick={handleClose}
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.05)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangePassword}
              disabled={loading || !isChangePasswordFormValid}
              variant="contained"
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#FF7700',
                '&:hover': {
                  bgcolor: '#E56900'
                },
                '&:disabled': {
                  bgcolor: 'rgba(255,119,0,0.3)',
                  color: 'rgba(255,255,255,0.5)'
                }
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Changing...
                </Box>
              ) : (
                'Change Password'
              )}
            </Button>
          </>
        )}

        {activeStep === 1 && (
          <>
            <Button
              onClick={handleBack}
              disabled={loading}
              startIcon={<ArrowBack />}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.05)'
                }
              }}
            >
              Back
            </Button>
            <Button
              onClick={handleSendOTP}
              disabled={loading || !forgotPasswordForm.email || otpCooldown > 0}
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#FF7700',
                '&:hover': {
                  bgcolor: '#E56900'
                },
                '&:disabled': {
                  bgcolor: 'rgba(255,119,0,0.3)',
                  color: 'rgba(255,255,255,0.5)'
                }
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Sending...
                </Box>
              ) : (
                'Send OTP'
              )}
            </Button>
          </>
        )}

        {activeStep === 2 && (
          <>
            <Button
              onClick={handleBack}
              disabled={loading}
              startIcon={<ArrowBack />}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.05)'
                }
              }}
            >
              Back
            </Button>
            <Button
              onClick={handleVerifyOTP}
              disabled={loading || !forgotPasswordForm.otp || forgotPasswordForm.otp.length !== 6}
              variant="contained"
              endIcon={<ArrowForward />}
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#FF7700',
                '&:hover': {
                  bgcolor: '#E56900'
                },
                '&:disabled': {
                  bgcolor: 'rgba(255,119,0,0.3)',
                  color: 'rgba(255,255,255,0.5)'
                }
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Verifying...
                </Box>
              ) : (
                'Verify OTP'
              )}
            </Button>
          </>
        )}

        {activeStep === 3 && (
          <>
            <Button
              onClick={handleBack}
              disabled={loading}
              startIcon={<ArrowBack />}
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'rgba(0,0,0,0.05)'
                }
              }}
            >
              Back
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={loading || !isResetPasswordFormValid}
              variant="contained"
              sx={{ 
                borderRadius: 2,
                px: 4,
                py: 1,
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: '#FF7700',
                '&:hover': {
                  bgcolor: '#E56900'
                },
                '&:disabled': {
                  bgcolor: 'rgba(255,119,0,0.3)',
                  color: 'rgba(255,255,255,0.5)'
                }
              }}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Resetting...
                </Box>
              ) : (
                'Reset Password'
              )}
            </Button>
          </>
        )}

      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
